import Model, { attr, belongsTo } from "@ember-data/model";
import { service } from "@ember/service";
import type BioExamObservation from "parog-web/models/loinc";
import trimLoincCode from "parog-web/utils/trim-loinc-code";
import type BioExamResultsReceivedJournalEvent from "./bio-exam-results-received-journal-event";

export const DfgLoinc = "LOINC-62238-1";
export const RacLoinc = "LOINC-32294-1";

type OruStatusStringEnum =
  | undefined
  | "R"
  | "P"
  | "F"
  | "C"
  | "I"
  | "D"
  | "X"
  | "U";

interface OriginalLine {
  raw?: RawFromHprim | RawFromOru;
  copied_match_origin_uuid?: string;
  raw_label_options?: string;
}

export type RawFromHprim = {
  segs: string[];
};

export type ManuallyMatchedResult = BioExamResult & {
  originalLine: OriginalLine & { raw: RawFromHprim };
};

type RawFromOru = {
  label: string;
  value: string;
  unit: string;
  "oru-status"?: OruStatusStringEnum;
};

export default class BioExamResult extends Model {
  @service requestManager;

  @attr("boolean") declare _destroy: boolean;
  @belongsTo("bio-exam-results-received-journal-event", {
    async: false,
    inverse: "bioExamResults"
  })
  declare bioExamResultsReceivedJournalEvent: BioExamResultsReceivedJournalEvent;
  @attr("string") declare code: string;
  @attr("boolean") declare isDraft: boolean;
  @attr("string") declare label: string;
  @attr("string") declare matchType: string;
  @attr("string") declare normValInf: string;
  @attr("string") declare normValSupp: string;
  @attr("date") declare occurredOn: Date;
  @attr("string") declare operator: "equal-to" | "greater-than" | "lower-than";
  @attr() declare originalLine: OriginalLine;
  @attr("string") declare oruStatus: OruStatusStringEnum;
  @attr("string") declare qualitativeValueTranslation?: string;
  @attr("string") declare selectedRawUnit: string;
  @attr("string") declare unit: string;
  @attr("string") declare uuid: string;
  @attr("string") declare value: string;

  get bioExamObservation(): BioExamObservation | null | undefined {
    return this.store?.peekRecord("loinc", trimLoincCode(this.code));
  }

  get displayedValue() {
    let displayedValue;
    switch (this.operator) {
      case "greater-than":
        displayedValue = `≥ ${this.value}`;
        break;
      case "lower-than":
        displayedValue = `≤ ${this.value}`;
        break;
      default:
        //display the stored translation for qualitative values
        displayedValue = this.qualitativeValueTranslation ?? this.value;
        break;
    }

    return displayedValue;
  }

  /**
   * A coherent value is either:
   * - a string value if a string is expected. This is when hasTextValue is true.
   * - a number value if a number is expected.
   *   Some results are expected to be numbers but the parsing is not perfect yet and the value may contain text, which breaks Chart.js.
   *
   * This value is used to filter the displayed values in the graph.
   */
  get hasCoherentValue(): boolean {
    if (this.hasTextValue) return true;
    if (this.hasNoValueType) return true;
    if (!this.bioExamObservation) return true;
    // Passed this point this.value is expected to be a number.
    // parseFloat and +(something) may return a different content if this.value contains some text, meaning this is an incoherent value.
    return parseFloat(this.value) == +this.value;
  }

  get hasNoValueType(): boolean {
    return !this.bioExamObservation?.valueType;
  }

  get hasTextValue(): boolean {
    return ["string", "enum"].includes(
      this.bioExamObservation?.valueType || ""
    );
  }

  get isAdhoc(): boolean {
    return Boolean(this.code.match(/^ADHOC/)) || !this.bioExamObservation;
  }

  get matchedByExperience() {
    return this.matchType == "experience";
  }

  async convert(targetUnit: string): Promise<string> {
    const response = await this.requestManager.request({
      url: `/api/v1/bio_exam_results/${this.id}/convert`,
      method: "POST",
      body: JSON.stringify({
        target_unit: targetUnit
      })
    });
    return response.content.data.attributes.value;
  }

  isManualMatching(): this is ManuallyMatchedResult {
    return (this.originalLine?.raw as RawFromHprim)?.segs?.[0] == "MANUAL RES";
  }

  transformAsManualRes(): void {
    this.originalLine = {
      raw: {
        segs: [
          "MANUAL RES",
          "",
          "",
          "",
          this.value || "",
          this.unit || "",
          "",
          "",
          "",
          ""
        ]
      }
    };
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "bio-exam-result": BioExamResult;
  }
}
